import YouTube from '@u-wave/react-youtube'
import dayjs from 'dayjs'
import Head from 'next/head'
import Image from 'next/image'
import { VideoJsonLd } from 'next-seo'
import React, { useState } from 'react'
import SvgPlay from '#/assets/images/icons/play_arrow.svg'
import Banner from '#/components/Banner'
import Button from '#/components/Button'
import Link from '#/components/Link'
import Modal from '#/components/Modal'
import Page from '#/components/Page'
import { logEvent } from '#/services/analytics'
import cn from '#/utils/classnames'
import BreadCrumbs from '../AboutCompany/components/BreadCrumbs/BreadCrumbs'
import styles from './styles.module.scss'
// eslint-disable-next-line import/newline-after-import
import { AllAcademyProps, AcademyCardProps } from './types'
const duration = require('dayjs/plugin/duration')

dayjs.extend(duration)

export const NewsHeader = () => (
  <header className={styles['th-news-header']}>
    <BreadCrumbs additional='Room' homeName='Home' pageName='News' />
  </header>
)

export const NewsBanner = () => (
  <div className={styles['th-news-banner']}>
    <h1 className={styles['th-banner-title']}>TopHap in the News</h1>
  </div>
)

const logWhatsNew = () => {
  logEvent('select_content', {
    content_type: 'academy',
    item_id: 'whats_new'
  })
}
export const Card: React.FC<AcademyCardProps> = ({
  academyVideos,
  clampTitle,
  date,
  featuredImage,
  title,
  excerpt,
  onWatch
}) => {
  function handleWatch() {
    if (onWatch && academyVideos?.videoId) onWatch(academyVideos.videoId)

    logEvent('select_content', {
      content_type: 'academy',
      item_id: 'card',
      value: title
    })
  }
  const convertDuration = (duration?: string) => {
    if (!duration) return ''
    const parsedDuration = duration.split(':')
    return (dayjs.duration({
      seconds: Number(parsedDuration[1]),
      minutes: Number(parsedDuration[0])
    }).toISOString())
  }

  return (
    <div className={styles['th-academy-card']} onClick={handleWatch}>
      <VideoJsonLd
        description={excerpt ?? ''}
        duration={convertDuration(academyVideos?.duration)}
        embedUrl={`https://www.youtube.com/watch?v=${academyVideos?.videoId}`}
        keyOverride={`${title}-${Date.now()}`}
        name={title ?? ''}
        thumbnailUrls={[
          featuredImage.node.sourceUrl
        ]}
        uploadDate={date ?? ''}
      />
      <div className={styles['th-card-thumbnail']}>
        <Image
          alt={title || 'Thumbnail'}
          height={featuredImage.node.mediaDetails.height}
          src={featuredImage?.node?.sourceUrl}
          width={featuredImage.node.mediaDetails.width}
        />
      </div>
      <div className={styles['th-card-content']}>
        <h2 className={cn(styles['th-card-title'], clampTitle ? 'line-clamp-3 min-h-[114px]' : styles['th-feature-video-title'])}>{title}</h2>
        <div className={styles['th-card-duration']}>
          <SvgPlay />
          {academyVideos?.duration}
        </div>
        <div className={cn(styles['th-card-description'], 'line-clamp-3')}>{excerpt} <span className='whitespace-nowrap'>. . .</span></div>
      </div>
    </div>
  )
}
const VideoSection: React.FC<{
  clampTitle?: boolean
  tag: string
  title: string
  articles: AcademyCardProps[]
  isMobile: boolean
  setVideo: (video: string) => void
}> = ({ clampTitle, tag, title, articles, setVideo }) => (
  <div className='mb-16 text-center'>
    <h2 className='mb-3 text-3xl'>{title}</h2>
    <div className='mb-6 grid grid-cols-1 px-2 text-left sm:grid-cols-2 sm:px-0 lg:grid-cols-3'>
      {articles.map(e => {
        const article: AcademyCardProps = e
        return (
          <div key={e.title} className='my-2 px-1 md:px-2'>
            <Card
              {...article}
              clampTitle={clampTitle}
              onWatch={(video:string) => setVideo(video)}
            />
          </div>
        )
      })}
    </div>
    <Link href={`/academy/${tag}`}>
      <Button
        className='inline-flex h-[36px] items-center rounded-full border border-primary bg-white px-4 py-2 text-sm font-medium text-primary'
        type='button'
        onClick={logWhatsNew}
      >
        See All {title}
      </Button>
    </Link>
  </div>
)

const Academy: React.FC<AllAcademyProps> = (
  {
    allNews: {
      features: { nodes: featureArticles },
      howTos: { nodes: howToArticles }
    },
    isMobile
  }
) => {
  const [video, setVideo] = useState<string | undefined>()
  return (
    <Page className={styles['th-academy']} title='Get the most out of TopHap | TopHap Academy'>
      <Head>
        <meta
          key='description'
          content="Watch these tutorial videos that demonstrate how to use TopHap features. Start here if you're new to TopHap!"
          name='description'
        />
      </Head>
      <h1 className={styles['th-page-title']}>Welcome to TopHap Academy</h1>
      <p className={styles['th-page-description']}>
        TopHap Academy is always evolving with new content and videos and we
        urge you to explore it to learn more about TopHap’s features and
        functionality.
      </p>

      <Banner className='mx-auto -mt-4 mb-8 max-w-lg px-2 sm:px-0' text='We&apos;re releasing new features every week!'>
        <Link href='/whats-new'>
          <Button
            className='inline-flex h-[36px] items-center rounded-full border border-primary bg-white px-4 py-2 text-sm font-medium text-primary'
            type='button'
            onClick={logWhatsNew}
          >
            See what&apos;s new
          </Button>
        </Link>
      </Banner>
      <VideoSection
        articles={howToArticles}
        clampTitle
        isMobile={isMobile}
        setVideo={setVideo}
        tag='how-to'
        title='How To Videos'
      />
      <VideoSection
        articles={featureArticles}
        clampTitle={false}
        isMobile={isMobile}
        setVideo={setVideo}
        tag='feature'
        title='Feature Videos'
      />

      <Modal
        className={styles['th-video-modal']}
        closeButtonClassName='-top-2.5 -right-2.5 p-0.5 text-white border-2 bg-red-500 w-6 h-6 opacity-100'
        visible={!!video}
        onClose={() => setVideo(undefined)}
      >
        <div className={styles['th-video-container']}>
          <YouTube
            allowFullscreen
            className={styles['th-video']}
            height='100%'
            video={video}
            width='100%'
          />
        </div>
      </Modal>
    </Page>
  )
}

export default Academy
